/*
 * Functions in this sdk returns data from the static-data directory
 * network_links.json is a snapshot of network_links table from nwallet_markets DB
 * affiliate_networks.json is a snapshot of affiliate_networks table from nwallet_markets DB
 */

/* eslint-disable camelcase */
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';
import datadog from '@nerdwallet/datadog';

import { logInfo, logWarn } from './logging-utils';
import staticNetworkLinks from '../../server/static-data/network_links.json';
import staticAffiliates from '../../server/static-data/affiliate_networks.json';
import staticInsuranceLegacyIdsToUuids from '../../server/static-data/insurance-legacy-ids-to-uuids.json';
import staticInvestingBrokers from '../../server/static-data/investing-brokers.json';
import staticSMBOffers from '../../server/static-data/smb-offers.json';

// Returns redirect details for a network link from static json files
export const getStaticNetworkLinkData = (id, req) => {
  const networkLink = staticNetworkLinks[id];
  if (!networkLink) {
    return null;
  }
  const affiliateId = networkLink.network_id;
  const affiliate = staticAffiliates[affiliateId];
  if (!affiliate) {
    return null;
  }

  const { name, network_id, url_sponsored, sponsored, vertical } = networkLink;
  const partnerTrackingParam = affiliate.url_query_param;

  // Create a url for redirecting to a partner site
  const clickId =
    _.get(req, 'query.shopping_finish_id') ||
    _.get(req, 'query.finish_id') ||
    uuidv4().replace(/-/g, ''); // consistent w/ Redirect Service's uuid.uuid4().hex
  let destinationUrl = url_sponsored;
  // Append a tracking parameter and clickId if present (e.g. subid=<clickId>)
  if (partnerTrackingParam) {
    const separator = url_sponsored.includes('?') ? '&' : '?';
    destinationUrl = `${url_sponsored}${separator}${partnerTrackingParam}=${clickId}`;
  }

  datadog.increment(
    'interstitial_client.fetch_redirect_details.static.success',
    1,
    {
      tags: { network_link_id: id },
    }
  );

  // Returns object with same shape as a Redirect Service response
  return {
    monetizing: sponsored,
    network: network_id,
    slug: name,
    sponsored,
    unique_click_id: clickId,
    url: destinationUrl,
    vertical,
    // Network links have hardcoded values for the following fields
    marketplace_offer: null,
    offer_account_type: null,
    offer_name: null,
    offer_review_type: null,
    product_name: null,
    product_type: null,
    query_params: [],
    redirect_page_customizations: { custom_html_content: '' },
    sec_form_adv_url: null,
    sec_legal_name: null,
    trade_name: null,
  };
};

// Finds an affiliate id via a regex search of affiliate_networks.json
export const getAffiliateIdByRegex = (destinationUrl) => {
  if (!destinationUrl) {
    return null;
  }

  // Find the first affiliate network where destinationUrl matches signifier regex
  const matchedAffiliateKey = _.findKey(staticAffiliates, (affiliate) => {
    if (!affiliate.signifier) {
      return false; // otherwise any url will match against an empty signifier
    }
    const regex = RegExp(affiliate.signifier);
    return regex.test(destinationUrl);
  });

  return matchedAffiliateKey ? parseInt(matchedAffiliateKey, 10) : null;
};

// Returns data for an affiliate network from static json files
export const getStaticAffiliateData = (
  destinationUrl,
  affiliateId = null,
  req
) => {
  let id = affiliateId;
  let usedRegex = false;

  // Get affilate id by regex if necessary
  if (id == null) {
    id = getAffiliateIdByRegex(destinationUrl);
    usedRegex = true;
  }

  // Get affiliate network data
  if (id !== null) {
    const affiliate = staticAffiliates[id];
    if (affiliate) {
      logInfo(req, 'Find static affiliate network - success', {
        usedRegex,
        destinationUrl,
        affiliateId: id,
      });
      return {
        id,
        partnerTrackingParam: affiliate.url_query_param,
        clickIdPrefix: affiliate.click_id_prefix,
        trackingUrlPrefix: _.get(affiliate, 'tracking_url_prefix'),
      };
    }
  }

  // Failed
  logWarn(req, 'Find static affiliate network - fail', {
    usedRegex,
    destinationUrl,
    affiliateId: id,
  });

  return null;
};

// Returns uuid for a legacy insurance product id from static json files
export const getInsuranceUUID = (id, type) => {
  const insuranceUUID = _.get(staticInsuranceLegacyIdsToUuids, `${type}.${id}`);
  if (!insuranceUUID) {
    return null;
  }
  return insuranceUUID;
};

// Returns redirect details for a static Offer from a json file based on the route slug.
export const getStaticRedirectData = (routeSlug, id, req) => {
  let offerData = null;

  switch (routeSlug) {
    case 'smb-offer':
      offerData = staticSMBOffers[id];
      break;
    case 'investing-brokerage':
      offerData = staticInvestingBrokers[id];
      break;
    default:
      return null;
  }

  if (!offerData) {
    return null;
  }

  const {
    url,
    slug,
    sponsored,
    monetizing,
    network,
    redirect_page_customizations,
  } = offerData;
  let partnerTrackingParam = null;
  let networkId = null;

  if (network != null && network !== -9999) {
    networkId = network;
  }

  // Get static affiliate network data
  const affiliateNetwork = getStaticAffiliateData(url, networkId, req);
  if (affiliateNetwork != null) {
    partnerTrackingParam = affiliateNetwork.partnerTrackingParam;
    networkId = affiliateNetwork.id;
  }

  // Create a url for redirecting to a partner site
  const clickId =
    _.get(req, 'query.shopping_finish_id') ||
    _.get(req, 'query.finish_id') ||
    uuidv4().replace(/-/g, '');
  let destinationUrl = url;

  // Append a tracking parameter and clickId if present (e.g. subid=<clickId>)
  if (partnerTrackingParam) {
    const separator = url.includes('?') ? '&' : '?';
    destinationUrl = `${url}${separator}${partnerTrackingParam}=${clickId}`;
  }

  //  object with same shape as a Redirect Service response
  const response = {
    monetizing,
    network: networkId,
    slug,
    sponsored,
    unique_click_id: clickId,
    url: destinationUrl,
    vertical: '',
    marketplace_offer: null,
    offer_account_type: null,
    offer_name: null,
    offer_review_type: null,
    product_name: null,
    product_type: null,
    query_params: [],
    redirect_page_customizations,
    sec_form_adv_url: null,
    sec_legal_name: null,
    trade_name: null,
  };

  logInfo(req, 'getStaticRedirectData success', { response });

  return response;
};
